import React, { useEffect, useState } from 'react';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Factory, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function WorkCentersPage() {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCenter, setEditCenter] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', machine_name: '', machine_asset_id: '', description: '', capacity_per_hour: 0, hourly_rate: 0 });

  useEffect(() => { loadCenters(); }, []);

  const loadCenters = async () => {
    try {
      const res = await inventoryAPI.listWorkCenters();
      setCenters(res.data);
    } catch (err) {
      toast.error('Failed to load work centers');
    } finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditCenter(null);
    setForm({ name: '', code: '', machine_name: '', machine_asset_id: '', description: '', capacity_per_hour: 0, hourly_rate: 0 });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditCenter(c);
    setForm({
      name: c.name, code: c.code, machine_name: c.machine_name || '',
      machine_asset_id: c.machine_asset_id || '', description: c.description || '',
      capacity_per_hour: c.capacity_per_hour || 0, hourly_rate: c.hourly_rate || 0
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.code) { toast.error('Name and code required'); return; }
    try {
      if (editCenter) {
        await inventoryAPI.updateWorkCenter(editCenter.id, form);
        toast.success('Work center updated');
      } else {
        await inventoryAPI.createWorkCenter(form);
        toast.success('Work center created');
      }
      setDialogOpen(false);
      loadCenters();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete work center "${c.name}"?`)) return;
    try {
      await inventoryAPI.deleteWorkCenter(c.id);
      toast.success('Work center deleted');
      loadCenters();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="work-centers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Work Centers</h1>
          <p className="text-xs text-muted-foreground">{centers.length} work centers</p>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="create-work-center-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Work Center
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="work-centers-table">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Code</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Name</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Machine</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Description</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Capacity/Hr</th>
                <th className="text-right p-2.5 text-xs font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8 text-xs text-muted-foreground">Loading...</td></tr>
              ) : centers.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-xs text-muted-foreground">No work centers</td></tr>
              ) : centers.map(c => (
                <tr key={c.id} className="border-b hover:bg-muted/50 transition-colors" data-testid={`wc-row-${c.code}`}>
                  <td className="p-2.5 font-mono text-xs font-medium">{c.code}</td>
                  <td className="p-2.5 text-xs font-medium">{c.name}</td>
                  <td className="p-2.5 text-xs">{c.machine_name || '-'}</td>
                  <td className="p-2.5 text-xs text-muted-foreground">{c.description || '-'}</td>
                  <td className="p-2.5 text-xs">{c.capacity_per_hour || '-'}</td>
                  <td className="p-2.5 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(c)} data-testid={`edit-wc-${c.code}`}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDelete(c)} data-testid={`delete-wc-${c.code}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editCenter ? 'Edit Work Center' : 'New Work Center'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                  placeholder="e.g. VMC-001" className="h-8 text-xs" data-testid="wc-name-input" />
              </div>
              <div>
                <Label className="text-xs">Code *</Label>
                <Input value={form.code} onChange={e => setForm(f => ({...f, code: e.target.value.toUpperCase()}))}
                  placeholder="e.g. VMC1" className="h-8 text-xs font-mono" disabled={!!editCenter} data-testid="wc-code-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Machine Name</Label>
              <Input value={form.machine_name} onChange={e => setForm(f => ({...f, machine_name: e.target.value}))}
                placeholder="e.g. Haas VF-2" className="h-8 text-xs" data-testid="wc-machine-input" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                className="h-8 text-xs" data-testid="wc-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Capacity/Hour</Label>
                <Input type="number" value={form.capacity_per_hour} onChange={e => setForm(f => ({...f, capacity_per_hour: parseFloat(e.target.value) || 0}))}
                  className="h-8 text-xs" data-testid="wc-capacity-input" />
              </div>
              <div>
                <Label className="text-xs">Hourly Rate</Label>
                <Input type="number" value={form.hourly_rate} onChange={e => setForm(f => ({...f, hourly_rate: parseFloat(e.target.value) || 0}))}
                  className="h-8 text-xs" data-testid="wc-rate-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-wc-btn">{editCenter ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
